import { Box, Button, TableCell, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import dayjs from 'dayjs';
import { ReactComponent as ReportsExcelIcon } from '~/assets/icons/reports-excel.svg';
import BaseTableReport from './components/base-table-report/BaseTableReport';
import { buildReportExcelFileName, downloadBc01ReportExcel } from './components/excelExport';
import { BaseDatePicker } from '~/components/common';
import reportApi, { parseBc01ResponsePayload, type IBc01Params } from '~/apis/report';
import { useAppSelector } from '~/redux/hooks';
import { handleApiError, notify } from '~/utils/helper';
import type { SortDirection } from '~/utils/types/common';
import { SortDirectionEnum } from '~/utils/enum';
import {
  mapJourneyStatusColumnInRows,
  resolveJourneySegmentStatusCell,
  transformBc01HeadCells,
  transformBc01Rows,
  withJourneySttColumnLayout,
  isJourneySttColumnLabel,
  type Bc01TableRow,
} from './components/constants';
import { ReportTableMultilineCell } from './components/ReportTableMultilineCell';
import ReportSubjectFilter from './components/ReportSubjectFilter';
import { trackReportFetchEnd, trackReportFetchStart } from './components/reportFetchLoading';
import styles from './Report.module.scss';

const cx = classNames.bind(styles);

const REPORT_TABLE_CELL_CLASS = 'text-base font-normal text-left align-top whitespace-normal';

function getDefaultJourneyDateRange(): { start: Date; end: Date } {
  return {
    start: dayjs().startOf('day').toDate(),
    end: dayjs().endOf('day').toDate(),
  };
}

function formatBc01FromTo(startMs: number, endMs: number): { from: string; to: string } {
  const a = dayjs(startMs);
  const b = dayjs(endMs);
  const min = a.isBefore(b, 'day') ? a : b;
  const max = a.isAfter(b, 'day') ? a : b;
  return {
    from: min.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    to: max.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
  };
}

const BC04_FETCH_DEBOUNCE_MS = 500;

type Bc04ApiSnapshot = {
  headers: string[];
  data: string[][];
};

const ReportJourney = () => {
  const { t, i18n } = useTranslation('admin/report');
  const { t: tCommon } = useTranslation('admin/common');
  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);
  const username = selectedUser?.username ?? userProfile?.username;

  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [listReady, setListReady] = useState(false);
  const [personsCount, setPersonsCount] = useState(0);

  const defaultRange = useMemo(() => getDefaultJourneyDateRange(), []);
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
    start: defaultRange.start,
    end: defaultRange.end,
  });

  const [bc04Snapshot, setBc04Snapshot] = useState<Bc04ApiSnapshot>(() => ({ headers: [], data: [] }));
  const [reportLoading, setReportLoading] = useState(false);
  const lastFetchKeyRef = useRef<string>('');
  const reportFetchInFlightRef = useRef(0);

  const [sortBy, setSortBy] = useState<string>();
  const [sortOrder, setSortOrder] = useState<SortDirection>(SortDirectionEnum.ASC);

  const hasBc04FromApi = bc04Snapshot.headers.length > 0;

  const headCells = useMemo(() => {
    const base = hasBc04FromApi ? transformBc01HeadCells(bc04Snapshot.headers) : [];
    return withJourneySttColumnLayout(base);
  }, [hasBc04FromApi, bc04Snapshot.headers]);

  const journeySttDataColumnId = useMemo(() => {
    if (!hasBc04FromApi || bc04Snapshot.headers.length === 0) return null;
    return isJourneySttColumnLabel(bc04Snapshot.headers[0]) ? 'col_0' : null;
  }, [hasBc04FromApi, bc04Snapshot.headers]);

  const trangThaiHeaderLabel = useMemo(() => t('journey.columns.trangThai'), [t]);
  const diaChiHeaderLabel = useMemo(() => t('journey.columns.diaChi'), [t]);

  const journeyStatusColumnId = useMemo(() => {
    if (!hasBc04FromApi || bc04Snapshot.headers.length === 0) return 'trangThai';
    const vi = trangThaiHeaderLabel.trim().toLowerCase();
    const idx = bc04Snapshot.headers.findIndex((h) => {
      const x = h.trim().toLowerCase();
      return x === vi || /trạng\s*thái/i.test(h) || /^status$/i.test(x);
    });
    return `col_${idx >= 0 ? idx : bc04Snapshot.headers.length - 1}`;
  }, [hasBc04FromApi, bc04Snapshot.headers, trangThaiHeaderLabel]);

  const journeyAddressColumnId = useMemo(() => {
    if (!hasBc04FromApi || bc04Snapshot.headers.length === 0) return 'diaChi';
    const vi = diaChiHeaderLabel.trim().toLowerCase();
    const idx = bc04Snapshot.headers.findIndex((h) => {
      const x = h.trim().toLowerCase();
      return x === vi || /địa\s*chỉ/i.test(h) || /^address$/i.test(x);
    });
    return idx >= 0 ? `col_${idx}` : null;
  }, [hasBc04FromApi, bc04Snapshot.headers, diaChiHeaderLabel]);

  const transformedRows = useMemo(() => {
    if (!hasBc04FromApi) return [];
    return transformBc01Rows(bc04Snapshot.headers, bc04Snapshot.data);
  }, [bc04Snapshot.headers, bc04Snapshot.data, hasBc04FromApi]);

  const defaultEmptyMessage = !selectedPersonId ? t('filters.selectSubjectToViewReport') : undefined;

  const displayRows = useMemo(() => {
    let rows = transformedRows;
    if (rows.length === 0 && !hasBc04FromApi) {
      // Khi chưa chọn đối tượng, hiển thị empty state thay vì row giả.
      rows = defaultEmptyMessage ? [] : [{ id: 'placeholder' } as Bc01TableRow];
    }
    if (!sortBy) return rows;
    const mul = sortOrder === SortDirectionEnum.ASC ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = String(a[sortBy] ?? '');
      const vb = String(b[sortBy] ?? '');
      return va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' }) * mul;
    });
  }, [transformedRows, sortBy, sortOrder, hasBc04FromApi, defaultEmptyMessage]);

  const reportRangeStartMs = dateRange.start !== null ? dateRange.start.getTime() : null;
  const reportRangeEndMs = dateRange.end !== null ? dateRange.end.getTime() : null;

  const calendarLabels = useMemo(
    () => ({
      buttonLabel: tCommon('calendarPicker.buttonLabel'),
      timeLabel: tCommon('calendarPicker.timeLabel'),
      clickOrDragToSelect: tCommon('calendarPicker.clickOrDragToSelect'),
      dateInputHint: tCommon('calendarPicker.clickOrDragToSelect'),
      invalidDateFormat: tCommon('calendarPicker.invalidDateFormat'),
      startDatePlaceholder: tCommon('calendarPicker.startDate'),
      endDatePlaceholder: tCommon('calendarPicker.endDate'),
      previousMonth: tCommon('calendarPicker.previousMonth'),
      nextMonth: tCommon('calendarPicker.nextMonth'),
      todayNote: tCommon('calendarPicker.todayNote'),
      invalidDateRange: tCommon('calendarPicker.invalidDateRange'),
      endBeforeStart: tCommon('calendarPicker.endBeforeStart'),
      futureDateNotAllowed: tCommon('calendarPicker.futureDateNotAllowed'),
      weekdays: [
        tCommon('calendarPicker.weekdays.mon'),
        tCommon('calendarPicker.weekdays.tue'),
        tCommon('calendarPicker.weekdays.wed'),
        tCommon('calendarPicker.weekdays.thu'),
        tCommon('calendarPicker.weekdays.fri'),
        tCommon('calendarPicker.weekdays.sat'),
        tCommon('calendarPicker.weekdays.sun'),
      ] as [string, string, string, string, string, string, string],
    }),
    [tCommon]
  );

  useEffect(() => {
    if (!username || reportRangeStartMs === null || reportRangeEndMs === null) {
      setBc04Snapshot({ headers: [], data: [] });
      lastFetchKeyRef.current = '';
      return;
    }

    if (!listReady || personsCount === 0) {
      return;
    }

    if (!selectedPersonId) {
      setBc04Snapshot({ headers: [], data: [] });
      lastFetchKeyRef.current = '';
      return;
    }

    const personsParam: string[] = [selectedPersonId];
    const nextKey = `${username}|${reportRangeStartMs}|${reportRangeEndMs}|${personsParam.join(',')}`;
    if (nextKey === lastFetchKeyRef.current) return;

    let stale = false;
    const startMs = reportRangeStartMs;
    const endMs = reportRangeEndMs;

    const timer = window.setTimeout(() => {
      (async () => {
        trackReportFetchStart(reportFetchInFlightRef);
        setReportLoading(true);
        try {
          const { from, to } = formatBc01FromTo(startMs, endMs);
          const params: IBc01Params = {
            username,
            from,
            to,
            persons: personsParam,
          };
          const res = await reportApi.getBc04(params);
          if (stale) return;
          const { headers, data: rows } = parseBc01ResponsePayload(res.data);
          setBc04Snapshot({ headers, data: rows });
          lastFetchKeyRef.current = nextKey;
        } catch (e: unknown) {
          if (stale) return;
          if (axios.isCancel(e)) return;
          setBc04Snapshot({ headers: [], data: [] });
          handleApiError(e);
        } finally {
          trackReportFetchEnd(reportFetchInFlightRef, setReportLoading);
        }
      })();
    }, BC04_FETCH_DEBOUNCE_MS);

    return () => {
      stale = true;
      window.clearTimeout(timer);
    };
  }, [username, reportRangeStartMs, reportRangeEndMs, selectedPersonId, listReady, personsCount]);

  const handleDateRangeChange = useCallback((start: Date | null, end: Date | null) => {
    setDateRange({ start, end });
  }, []);

  const handleStartDateChange = useCallback(
    (date: string | null) => {
      const start = date ? dayjs(date).toDate() : null;
      handleDateRangeChange(start, dateRange.end);
    },
    [dateRange.end, handleDateRangeChange]
  );

  const handleEndDateChange = useCallback(
    (date: string | null) => {
      const end = date ? dayjs(date).toDate() : null;
      handleDateRangeChange(dateRange.start, end);
    },
    [dateRange.start, handleDateRangeChange]
  );

  const handleClearFilters = useCallback(() => {
    const d = getDefaultJourneyDateRange();
    setSelectedPersonId(null);
    setDateRange({ start: d.start, end: d.end });
  }, []);

  const handleExport = useCallback(() => {
    const { headers, data } = bc04Snapshot;
    if (data.length === 0) {
      notify('warning', t('exportNoData'));
      return;
    }
    const fileName = buildReportExcelFileName(t('journey.exportFileName'));
    const exportData = mapJourneyStatusColumnInRows(headers, data, i18n.language);
    queueMicrotask(() => {
      downloadBc01ReportExcel(headers, exportData, { fileName });
    });
  }, [bc04Snapshot, t, i18n.language]);

  const handleRequestSort = useCallback((sortField: string, order: SortDirection) => {
    setSortBy(sortField);
    setSortOrder(order);
  }, []);

  const renderCell = useCallback(
    (row: Bc01TableRow) => {
      const cell = REPORT_TABLE_CELL_CLASS;
      const statusRaw = String(row[journeyStatusColumnId as string] ?? '');
      const { displayText: statusDisplay, rowColor } = resolveJourneySegmentStatusCell(statusRaw, i18n.language);

      return (
        <>
          {headCells.map((hc) => {
            const raw = String(row[hc.id as string] ?? '');
            const isStatus = hc.id === journeyStatusColumnId;
            const display = isStatus ? statusDisplay : raw;
            const useWrap = hc.id === journeyAddressColumnId || (journeyAddressColumnId == null && hc.id === 'diaChi');
            const layoutClass = useWrap ? cx('journey-cell-wrap') : cx('journey-cell-nowrap');
            const isStt = journeySttDataColumnId != null && hc.id === journeySttDataColumnId;

            return (
              <TableCell
                key={hc.id}
                className={`${cell} ${layoutClass}${isStt ? ` ${cx('journey-stt-data-cell')}` : ''}`}
                sx={{
                  color: rowColor,
                  ...(isStt ? { verticalAlign: 'middle' } : {}),
                }}
              >
                <ReportTableMultilineCell text={display} />
              </TableCell>
            );
          })}
        </>
      );
    },
    [headCells, journeyStatusColumnId, journeyAddressColumnId, journeySttDataColumnId, i18n.language]
  );

  return (
    <Box className={cx('report-screen', 'journey-report-page')}>
      <Box
        className='flex justify-between items-center h-20 px-6'
        sx={{
          background: 'var(--Brand-Primary-color_brand_primary, #123B6D)',
        }}
      >
        <Typography className='font-semibold text-xl' sx={{ color: '#fff' }}>
          {t('journey.title')}
        </Typography>
        <Box className={cx('report-page-header-actions')}>
          <Button
            className={cx('export-button')}
            variant='contained'
            disableElevation
            endIcon={<ReportsExcelIcon className={cx('export-button-icon')} width={20} height={20} />}
            onClick={handleExport}
          >
            <span className={cx('export-button-label')}>{t('export')}</span>
          </Button>
        </Box>
      </Box>

      <Box className={cx('report-filter-row')}>
        <ReportSubjectFilter
          username={username}
          triggerId='report-journey-subject-trigger'
          selectedPersonId={selectedPersonId}
          onSelectedPersonIdChange={setSelectedPersonId}
          onListReadyChange={(ready, count) => {
            setListReady(ready);
            setPersonsCount(count);
          }}
        />

        <Box className={cx('report-filter-field')}>
          <span className={cx('report-filter-field-label')}>{t('filters.time')}</span>
          <Box className='flex flex-wrap items-center gap-3'>
            <Box sx={{ flex: '1 1 220px', minWidth: 180 }}>
              <BaseDatePicker
                uiVariant='calendarPicker'
                portalId='datePicker-portalId'
                className='w-full'
                placeholder={calendarLabels.startDatePlaceholder}
                value={dateRange.start ? dayjs(dateRange.start).format('YYYY-MM-DD') : undefined}
                onChange={handleStartDateChange}
                dateFormat='YYYY-MM-DD'
                calendarProps={{ popperClassName: styles.reportDatePickerPopper }}
              />
            </Box>
            <Box className='text-[#7C8796]'>-</Box>
            <Box sx={{ flex: '1 1 220px', minWidth: 180 }}>
              <BaseDatePicker
                uiVariant='calendarPicker'
                portalId='datePicker-portalId'
                className='w-full'
                placeholder={calendarLabels.endDatePlaceholder}
                value={dateRange.end ? dayjs(dateRange.end).format('YYYY-MM-DD') : undefined}
                onChange={handleEndDateChange}
                dateFormat='YYYY-MM-DD'
                calendarProps={{ popperClassName: styles.reportDatePickerPopper }}
              />
            </Box>
          </Box>
        </Box>

        <Box className={cx('report-filter-clear')}>
          <span className={cx('report-filter-field-label')} aria-hidden>
            &nbsp;
          </span>
          <Button variant='text' className={cx('clear-filter-button')} onClick={handleClearFilters}>
            {t('filters.clearFilters')}
          </Button>
        </Box>
      </Box>

      <Box className={`px-6 ${cx('report-table-wrap')}`} sx={{ position: 'relative' }}>
        {reportLoading && (
          <Box className={cx('report-table-status-overlay', 'report-table-status-overlay--journey')}>
            <Typography className='text-[#404854]'>{t('tableLoading')}</Typography>
          </Box>
        )}
        <BaseTableReport<Bc01TableRow>
          scrollable
          hidePagination
          data={displayRows}
          headCells={headCells}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onRequestSort={handleRequestSort}
          renderCell={(row) => renderCell(row)}
          emptyMessage={defaultEmptyMessage}
          suppressEmptyState={reportLoading}
        />
      </Box>
    </Box>
  );
};

export default ReportJourney;
